import { useMemo, type ReactNode } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { LumanityProvider } from "@lumanity/ui";
import { getEnv } from "@/config/env";
import { useAuth } from "@/contexts/AuthContext";

const API_BASE_URL = getEnv("VITE_API_BASE_URL") || "http://localhost:8010";

/**
 * Wires @lumanity/ui to PubCreator: same token as the rest of the app,
 * PubCreator's own QueryClient (so the library's queries show up in the same
 * cache), and every service reached through PubCreator's backend:
 *
 * - data-ingestion / evidence-base / content-intelligence / prompt-library
 *   through the gateway (`/api/ingestion|evidence|intelligence|prompts/*`)
 * - project-service calls through its pass-through (`/api/project-service`)
 */
export function PubCreatorLumanityProvider({ queryClient, children }: { queryClient: QueryClient; children: ReactNode }) {
  const { getAccessToken } = useAuth();
  const config = useMemo(
    () => ({
      apiBaseUrl: API_BASE_URL,
      dataIngestionApiUrl: API_BASE_URL,
      evidenceBaseApiUrl: API_BASE_URL,
      contentIntelligenceApiUrl: API_BASE_URL,
      promptLibraryApiUrl: API_BASE_URL,
      projectsApiUrl: `${API_BASE_URL}/api/project-service`,
      getAccessToken,
    }),
    [getAccessToken],
  );
  return (
    <LumanityProvider config={config} queryClient={queryClient}>
      {children}
    </LumanityProvider>
  );
}
